import { EyeOff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { GuideCard } from '@/components/GuideCard';
import { useGuidesAvailability } from '@/hooks/useGuideAvailability';
import { useActiveGuides } from '@/hooks/useGuides';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { cn } from '@/lib/utils';
import { PageNotFound } from '@/pages/PageNotFound';
import {
  guideFilterSpecialties,
  guideSpecialtyLabels,
  type GuideSpecialtyType,
  type GuideType,
} from '@/types/types_guide';
import { todayCalendarDate } from '@/util/util_format';
import { GuideProfileDialog } from './GuideProfileDialog';

type SpecialtyFilter = GuideSpecialtyType | 'all';

/**
 * The public Tour Leaders roster, ported from the static `tour-leaders.html`.
 *
 * Two sources feed it and they fail independently, which is why the availability
 * call is not allowed to gate the render: the profiles are ours, out of
 * Firestore, and the diaries are /portal's. A guide with no portal link — or a
 * portal that is down — still gets a card and a profile, with availability shown
 * as on request.
 */
export function PageGuides() {
  const { isAdmin } = useAuth();
  const { settings, loading: loadingSettings } = useSiteSettings();
  const { guides, loading } = useActiveGuides();
  const [specialty, setSpecialty] = useState<SpecialtyFilter>('all');
  const [openGuide, setOpenGuide] = useState<GuideType | null>(null);

  const portalGuideIds = useMemo(
    () => guides.map((g) => g.portalGuideId).filter(Boolean),
    [guides],
  );
  const { diaries, loading: loadingAvailability, error, fromDate, toDate } =
    useGuidesAvailability(portalGuideIds);

  const today = todayCalendarDate();

  // Only the specialties somebody on the roster actually holds — a filter that
  // can only ever return nothing is worse than no filter, as on the catalogue.
  const specialtiesPresent = useMemo(() => {
    const held = new Set(guides.flatMap((g) => g.specialties));
    return guideFilterSpecialties.filter((s) => held.has(s));
  }, [guides]);

  const filtered = specialty === 'all' ? guides : guides.filter((g) => g.specialties.includes(specialty));

  const availableToday = useMemo(
    () =>
      guides.filter((g) => {
        const diary = g.portalGuideId ? diaries.get(g.portalGuideId) : undefined;
        return diary?.has(today) ?? false;
      }).length,
    [guides, diaries, today],
  );

  // The section is switched off (see `SiteSectionsCard`). Hiding the nav links
  // is not enough on its own — a concierge who bookmarked this page last week
  // would walk straight past them — so the route closes too, and answers exactly
  // as an address with nothing at it would. Admins are the exception, and keep
  // reaching it so the thing can be looked at before it is turned on.
  if (!loadingSettings && !settings.tourLeadersVisible && !isAdmin) {
    return <PageNotFound fullScreen={false} />;
  }

  return (
    <div>
      {/* What an admin is looking at, when everybody else would get a 404. */}
      {!settings.tourLeadersVisible && isAdmin && (
        <div className='tw:flex tw:items-center tw:justify-center tw:gap-2 tw:bg-ed-umber/15 tw:px-6 tw:py-2.5 tw:text-sm tw:text-ed-umber'>
          <EyeOff className='tw:size-4 tw:shrink-0' />
          <span>
            Hidden from partners — visible to Eighty Days admins only. Turn it back on from the{' '}
            <Link to='/admin' className='tw:underline tw:underline-offset-2'>
              dashboard
            </Link>
            .
          </span>
        </div>
      )}

      {/* Hero */}
      <section className='tw:bg-ed-black tw:px-6 tw:pt-16 tw:pb-12'>
        <div className='tw:mx-auto tw:max-w-6xl tw:text-center'>
          <p className='tw:text-xs tw:tracking-[0.3em] tw:text-ed-gold tw:uppercase'>Expert Local Knowledge</p>
          <h1 className='tw:mt-4 tw:font-serif tw:text-5xl tw:font-light tw:text-ed-cream'>Tour Leaders</h1>
          <p className='tw:mx-auto tw:mt-4 tw:max-w-2xl tw:leading-relaxed tw:text-ed-cream/70'>
            Our curated network of expert tour leaders, each with their own specialties and deep local
            knowledge. Browse profiles, check availability, and find the right tour leader for your guests.
          </p>

          <div className='tw:mt-12 tw:flex tw:justify-center tw:gap-12'>
            <Stat value={guides.length} label='Tour Leaders' />
            {/* Absent until the diaries are in: a zero here reads as "nobody is
                free today", which is a very different thing from "not yet
                known". */}
            {!loadingAvailability && !error && diaries.size > 0 && (
              <Stat value={availableToday} label='Available Today' />
            )}
            <Stat value={specialtiesPresent.length} label='Specialties' />
          </div>
        </div>
      </section>

      {/* Specialty filter */}
      {specialtiesPresent.length > 0 && (
        <section className='tw:border-b tw:border-ed-umber/10 tw:bg-white tw:px-6 tw:py-6'>
          <div className='tw:mx-auto tw:flex tw:max-w-6xl tw:flex-wrap tw:items-center tw:justify-center tw:gap-3'>
            <span className='tw:mr-2 tw:text-sm tw:text-ed-stone'>Filter by specialty:</span>
            <FilterButton active={specialty === 'all'} onClick={() => setSpecialty('all')}>
              All Tour Leaders
            </FilterButton>
            {specialtiesPresent.map((s) => (
              <FilterButton key={s} active={specialty === s} onClick={() => setSpecialty(s)}>
                {guideSpecialtyLabels[s]}
              </FilterButton>
            ))}
          </div>
        </section>
      )}

      {/* Roster */}
      <section className='tw:mx-auto tw:max-w-6xl tw:px-6 tw:py-16'>
        {loading ? (
          <p className='tw:text-center tw:text-ed-stone'>Loading tour leaders…</p>
        ) : filtered.length === 0 ? (
          <p className='tw:text-center tw:text-ed-stone'>
            {guides.length === 0
              ? 'No tour leaders are listed yet.'
              : 'No tour leaders match that specialty. Try another.'}
          </p>
        ) : (
          <div className='tw:grid tw:gap-8 tw:sm:grid-cols-2 tw:lg:grid-cols-3'>
            {filtered.map((guide) => (
              <GuideCard
                key={guide.id}
                guide={guide}
                diary={guide.portalGuideId ? diaries.get(guide.portalGuideId) : undefined}
                today={today}
                onOpen={() => setOpenGuide(guide)}
              />
            ))}
          </div>
        )}
      </section>

      <GuideProfileDialog
        guide={openGuide}
        diary={openGuide?.portalGuideId ? diaries.get(openGuide.portalGuideId) : undefined}
        fromDate={fromDate}
        toDate={toDate}
        loadingAvailability={loadingAvailability}
        availabilityError={error}
        onClose={() => setOpenGuide(null)}
      />
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className='tw:text-center'>
      <p className='tw:font-serif tw:text-4xl tw:text-ed-gold'>{value}</p>
      <p className='tw:mt-1 tw:text-sm tw:tracking-wider tw:text-ed-cream/60 tw:uppercase'>{label}</p>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'tw:rounded-full tw:border tw:px-4 tw:py-2 tw:text-sm tw:transition-colors',
        active
          ? 'tw:border-ed-gold tw:bg-ed-gold tw:text-ed-black'
          : 'tw:border-ed-umber/30 tw:text-ed-charcoal tw:hover:border-ed-gold tw:hover:text-ed-gold',
      )}
    >
      {children}
    </button>
  );
}
